import { useEffect, useMemo, useState } from 'react';
import { db } from '../db/client';
import { resolveSelector, getPinPosition } from '../shared/selector';
import { PinPopover } from './PinPopover';
import { Pin } from './Pin';

interface PinLayerProps {
  orgId: string;
  currentUserId: string;
  currentUserEmail: string;
  focusCommentId: string | null;
  onFocused: () => void;
}

export function PinLayer({
  orgId,
  currentUserId,
  currentUserEmail,
  focusCommentId,
  onFocused,
}: PinLayerProps) {
  const u = new URL(window.location.href);
  const pageOrigin = u.origin;
  const pagePath = u.pathname;

  const { data } = db.useQuery({
    comments: {
      $: {
        where: { 'organization.id': orgId, pageOrigin, pagePath },
        order: { createdAt: 'asc' as const },
      },
      author: {},
      replies: { author: {} },
    },
  });

  const comments = useMemo(() => data?.comments ?? [], [data?.comments]);

  // Re-render pins when DOM mutates / scrolls / resizes so positions stay accurate.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf = 0;
    const bump = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTick((t) => t + 1));
    };
    window.addEventListener('scroll', bump, true);
    window.addEventListener('resize', bump);
    const mo = new MutationObserver(bump);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener('scroll', bump, true);
      window.removeEventListener('resize', bump);
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  const [openId, setOpenId] = useState<string | null>(null);

  // Auto-open and scroll to a focused comment if requested.
  useEffect(() => {
    if (!focusCommentId) return;
    const c = comments.find((x) => x.id === focusCommentId);
    if (!c) return;
    const el = resolveSelector({
      cssSelector: c.cssSelector,
      xpath: c.xpath,
      elementText: c.elementText,
    });
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setOpenId(focusCommentId);
    const timer = setTimeout(() => onFocused(), 3500);
    return () => clearTimeout(timer);
  }, [focusCommentId, comments, onFocused]);

  // Same-tab focus events from the side panel's comments list.
  useEffect(() => {
    const onFocus = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (!id) return;
      setOpenId(id);
      const c = comments.find((x) => x.id === id);
      if (!c) return;
      const el = resolveSelector({
        cssSelector: c.cssSelector,
        xpath: c.xpath,
        elementText: c.elementText,
      });
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.addEventListener('uinoter:focus-comment', onFocus);
    return () => window.removeEventListener('uinoter:focus-comment', onFocus);
  }, [comments]);

  // Resolve selectors per comment on each tick.
  type ResolvedPin = { comment: (typeof comments)[number]; left: number; top: number };
  const pins: ResolvedPin[] = useMemo(() => {
    const result: ResolvedPin[] = [];
    for (const c of comments) {
      const el = resolveSelector({
        cssSelector: c.cssSelector,
        xpath: c.xpath,
        elementText: c.elementText,
      });
      if (!el) continue;
      const pos = getPinPosition(el, c.pinX, c.pinY);
      result.push({ comment: c, ...pos });
    }
    return result;
    // tick is a re-render trigger, intentionally not in the data we map over
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, tick]);

  return (
    <>
      {pins.map((p) => (
        <Pin
          key={p.comment.id}
          comment={p.comment}
          left={p.left}
          top={p.top}
          focused={p.comment.id === focusCommentId}
          open={p.comment.id === openId}
          onClick={() => setOpenId(p.comment.id === openId ? null : p.comment.id)}
        />
      ))}

      {pins.map((p) =>
        p.comment.id === openId ? (
          <PinPopover
            key={`pop-${p.comment.id}`}
            comment={p.comment}
            currentUserId={currentUserId}
            currentUserEmail={currentUserEmail}
            anchorLeft={p.left}
            anchorTop={p.top}
            onClose={() => setOpenId(null)}
          />
        ) : null,
      )}
    </>
  );
}
